import type { PositionVM } from '~/composables/useRequest/requests/data-contracts';


const CACHE_KEY_ENTRIES = 'account-positions-entries';
const CACHE_TTL_MS = 5_000;

type PositionParams = {
  treeId?: number;
  periodId?: number;
  offset?: number;
  properties?: string[];
};

type CacheEntry = {
  data: PositionVM[];
  promise: Promise<PositionVM[]>;
  loadedAt: number | null;
  isLoading: boolean;
  ttlTimer: ReturnType<typeof setTimeout> | null;
};

/**
 * Общий кэш account position.
 * ensureLoaded всегда возвращает копию — clear() не затрагивает данные в компонентах.
 */
export default function accountPositionsData() {
  const { account } = useRequest();

  const entries = useState<Record<string, CacheEntry>>(CACHE_KEY_ENTRIES, () => ({}));

  const buildCacheKey = (accountId: number, params: PositionParams) =>
    [
      accountId,
      params.treeId ?? '',
      params.periodId ?? '',
      params.offset ?? '',
      (params.properties ?? []).join(','),
    ].join(':');

  const clone = (list: PositionVM[]) => list.slice();

  const clearEntry = (key: string) => {
    const entry = entries.value[key];

    if (!entry) {
      return;
    }

    if (entry.ttlTimer != null) {
      clearTimeout(entry.ttlTimer);
    }

    entries.value = Object.fromEntries(
      Object.entries(entries.value).filter(([entryKey]) => entryKey !== key),
    );
  };

  const clear = () => {
    Object.keys(entries.value).forEach(clearEntry);
    entries.value = {};
  };

  const scheduleClear = (key: string) => {
    const entry = entries.value[key];

    if (!entry) {
      return;
    }

    if (entry.ttlTimer != null) {
      clearTimeout(entry.ttlTimer);
    }

    entry.ttlTimer = setTimeout(() => {
      clearEntry(key);
    }, CACHE_TTL_MS);
  };

  const isFresh = (entry: CacheEntry) =>
    entry.loadedAt != null && Date.now() - entry.loadedAt < CACHE_TTL_MS;

  const ensureLoaded = (
    accountId: number,
    params: PositionParams = {},
    mode: 'soft' | 'hard' = 'soft',
  ): Promise<PositionVM[]> => {
    const key = buildCacheKey(accountId, params);
    const existing = entries.value[key];

    if (mode === 'soft' && existing) {
      if (existing.isLoading || isFresh(existing)) {
        return existing.promise.then(clone);
      }

      clearEntry(key);
    }

    if (mode === 'hard') {
      clearEntry(key);
    }

    const entry: CacheEntry = {
      data: [],
      promise: Promise.resolve([]),
      loadedAt: null,
      isLoading: true,
      ttlTimer: null,
    };

    const promise = (async () => {
      try {
        const { payload } = await account.accountPositionControllerGetPosition(accountId, {
          treeId: params.treeId,
          periodId: params.periodId,
          offset: params.offset,
          properties: params.properties,
        });

        if (entries.value[key] !== entry) {
          return clone(payload);
        }

        entry.data = clone(payload);
        entry.loadedAt = Date.now();
        entry.isLoading = false;
        scheduleClear(key);

        return clone(payload);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch account positions:', error);

        // Do not cache failures — next soft call must retry
        if (entries.value[key] === entry) {
          clearEntry(key);
        }

        return [];
      }
    })();

    entry.promise = promise;
    entries.value = { ...entries.value, [key]: entry };

    return promise.then(clone);
  };

  return {
    ensureLoaded,
    clear,
  };
}
